import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Footer } from "../components/layout/Footer";
import { Navbar } from "../components/layout/Navbar";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

const Contact = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Initialize fade-in animations
  useEffect(() => {
    const fadeElements = document.querySelectorAll(".fade-in-section");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      fadeElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{t("meta.contact.title")}</title>
        <meta name="description" content={t("meta.contact.description")} />
        <meta name="keywords" content={t("meta.contact.keywords")} />
        <meta property="og:title" content={t("meta.contact.title")} />
        <meta property="og:description" content={t("meta.contact.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gugudata.com/contact" />
        <link rel="canonical" href="https://gugudata.com/contact" />
        <html lang={language} />
      </Helmet>

      <Navbar />

      {/* Contact Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="fade-in-section">
            <div className="text-center mb-20">
              <h1 className="text-5xl md:text-6xl font-bold mb-4 text-gray-900 dark:text-white">{t("contact.title")}</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto font-light tracking-wide">{t("contact.subtitle")}</p>
            </div>

            {/* Company Introduction */}
            <div className="max-w-4xl mx-auto mb-24">
              <div className="prose dark:prose-invert max-w-none">
                {/* Introduction Text */}
                <div className="mb-20 max-w-3xl mx-auto">
                  <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 font-light tracking-wide text-center">{t("contact.intro.paragraph1")}</p>
                </div>

                {/* Feature Cards */}
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="group">
                    <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
                      <h3 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white">{t("contact.sections.coreAdvantages")}</h3>
                      <p className="text-gray-600 dark:text-gray-400 font-light leading-7 flex-grow" style={{ fontSize: "15px" }}>
                        {t("contact.intro.paragraph2")}
                      </p>
                    </div>
                  </div>

                  <div className="group">
                    <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
                      <h3 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white">{t("contact.sections.technicalFeatures")}</h3>
                      <p className="text-gray-600 dark:text-gray-400 font-light leading-7 flex-grow" style={{ fontSize: "15px" }}>
                        {t("contact.intro.paragraph3")}
                      </p>
                    </div>
                  </div>

                  <div className="group">
                    <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow duration-200">
                      <h3 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white">{t("contact.sections.solutions")}</h3>
                      <p className="text-gray-600 dark:text-gray-400 font-light leading-7 flex-grow" style={{ fontSize: "15px" }}>
                        {t("contact.intro.paragraph4")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bottom Text */}
                <div className="mt-20 max-w-3xl mx-auto">
                  <div className="text-center space-y-6">
                    <p className="text-gray-600 dark:text-gray-400 font-light tracking-wide leading-relaxed">{t("contact.intro.paragraph5")}</p>
                    <p className="text-gray-600 dark:text-gray-400 font-light tracking-wide leading-relaxed">{t("contact.intro.paragraph6")}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="max-w-2xl mx-auto">
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-12 text-gray-900 dark:text-white">{t("contact.info.title")}</h2>
                <div className="flex flex-col items-center space-y-8">
                  <div className="inline-flex items-center space-x-2">
                    <span className="w-2 h-2 bg-brand-blue rounded-full"></span>
                    <span className="text-gray-900 dark:text-white font-medium">{t("contact.info.company")}</span>
                  </div>

                  <div className="flex flex-col items-center text-gray-600 dark:text-gray-400 font-light space-y-1">
                    <span>{t("contact.info.address")}</span>
                  </div>

                  <div className="w-12 h-px bg-gray-200 dark:bg-gray-800"></div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="flex flex-col items-center">
                      <a
                        href="mailto:support@gugudata.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 w-[280px] justify-center whitespace-nowrap"
                      >
                        <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Email
                      </a>
                      <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">support@gugudata.ai</span>
                    </div>

                    <div className="flex flex-col items-center">
                      <a
                        href="https://gugudata-workspace.slack.com/archives/C08KP9XEC73"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 w-[280px] justify-center whitespace-nowrap"
                      >
                        <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                        </svg>
                        Get Support on Slack
                      </a>
                      <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">GuGuData Workspace</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
